import {
  Checkbox,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  Box,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import Page from "src/components/Page";
import {
  FormProvider,
  RHFSelect,
  RHFTextField,
} from "src/components/hook-form";
import useSettings from "src/hooks/useSettings";
import { PATH_DASHBOARD } from "src/routes/paths";
import {
  cartAdminAdd,
  masterGetDataByid,
  packageAdminConstituents,
  packageAdminList,
} from "../api";
import RHFAutocomplete from "src/components/hook-form/RHFAutocomplete";
import { useSnackbar } from "notistack";
import { fString } from "src/utils/formatNumber";
import { LoadingButton } from "@mui/lab";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

export function groupBy(array = [], key) {
  return array.reduce((result, currentItem) => {
    const group = currentItem[key];
    if (!result[group]) {
      result[group] = [];
    }

    result[group].push(currentItem);
    return result;
  }, {});
}

const defaultValues = {
  student: "",
  package: "",
  paymentMode: "",
  bankRefNo: "",
};
const schema = Yup.object().shape({
  paymentMode: Yup.string().trim().required("Required"),
  student: Yup.object()
    .shape()
    .nullable()
    .typeError("Required")
    .required("Required"),
  package: Yup.object()
    .shape()
    .nullable()
    .typeError("Required")
    .required("Required"),
  bankRefNo: Yup.string()
    .nullable()
    .notRequired()
    .test(
      "txn-format",
      "Invalid Transaction ID format for selected Payment Mode",
      function (value) {
        if (!value) return true;
        const { paymentMode } = this.parent;

        switch (paymentMode) {
          case "Credit Card":
          case "Debit Card":
          case "Net Banking":
            // alphanumeric + @ . - _ , length 6–20
            return /^[a-zA-Z0-9@._-]{6,20}$/.test(value);

          case "UPI":
            // UPI ka format alag hi rakhte hai
            return /^[a-zA-Z0-9._-]{2,30}@[a-zA-Z]{2,20}$/.test(value);

          default:
            return true;
        }
      }
    ),
});
export default function CreateOrder() {
  const theme = useTheme();
  const { themeStretch } = useSettings();
  const schoolID = JSON.parse(sessionStorage.getItem("schoolId"));
  const { enqueueSnackbar } = useSnackbar();
  const [packageDetail, setPackageDetail] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState([]);
  const [isMendatory, setIsMendatory] = useState(false);
  const [isAllOptional, setIsAllOptional] = useState(false);
  const [loading, setLoading] = useState(false);
  const [optionalQty, setOptionalQty] = useState({});
  const [selectedGroupItem, setSelectedGroupItem] = useState({});
  const [expandedCategory, setExpandedCategory] = useState("all");
  const [groupProduct, setGroupProduct] = useState([]);

  const methods = useForm({ defaultValues, resolver: yupResolver(schema) });
  const { handleSubmit, watch, reset, setValue } = methods;

  const studentWatch = watch("student");
  const packageWatch = watch("package");
  const paymentModeWatch = watch("paymentMode");

  const { data: studentList, isLoading } = useQuery({
    queryKey: ["getData", "students", 0, 10000, "schoolID", schoolID],
    queryFn: masterGetDataByid,
    enabled: !!schoolID,
  });
  const { data: packageList, isLoading: isLoadingPackage } = useQuery({
    queryKey: ["packages", studentWatch?.studentID],
    queryFn: packageAdminList,
    enabled: !!studentWatch?.studentID,
  });

  const fetchPackageDetails = async (value) => {
    try {
      setLoading(true);
      const data = await packageAdminConstituents(value?.packageID);
      const groupedData = groupBy(data, "categoryName");
      setPackageDetail(groupedData);
      setIsMendatory(
        data.every(
          (el) => el.optional === 1 || el?.optional === 2 || el?.optional === 5
        )
      );
      setIsAllOptional(
        data.every((el) => el.optional === 3 || el?.optional === 4)
      );
      setGroupProduct(data.filter((el) => el.optional === 5));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    if (expandedCategory === "all") {
      setExpandedCategory(category);
      return;
    }
    setExpandedCategory((prev) => (prev === category ? "all" : category));
  };

  const handleChangeSingle = (el) => {
    const checkIndex = selectedPackage.findIndex(
      (item) => item.productID === el.productID
    );
    if (checkIndex === -1) {
      const newData = [...selectedPackage, el];
      setSelectedPackage(newData);
      setOptionalQty((ps) => ({ ...ps, [el.productID]: el.qty }));
    } else {
      const filterData = selectedPackage.filter(
        (item) => item.productID !== el.productID
      );
      setSelectedPackage(filterData);
      // setOptionalQty((ps) => ({ ...ps, [el.productID]: '' }));
    }
  };

  const handleChangeAll = (e) => {
    // console.log(Object.values(packageDetail).flat(1));
    if (e.target.checked) {
      const selectedItem = [
        ...selectedPackage,
        ...Object.values(packageDetail)
          ?.flat(1)
          ?.filter((el) => el?.optional === 3 || el?.optional === 4),
      ];
      setSelectedPackage([...new Set(selectedItem)]);
      [...new Set(selectedItem)]?.forEach((el) => {
        setOptionalQty((ps) => ({ ...ps, [el.productID]: el?.qty }));
      });

      return;
    }
    setSelectedPackage([]);
    // setOptionalQty({});
  };

  const mutation = useMutation((req) => cartAdminAdd(req), {
    onSuccess: (response) => {
      enqueueSnackbar(response?.message || "Order created successfully", {
        variant: "success",
      });
      setOptionalQty({});
      setSelectedPackage([]);
      reset();
      setPackageDetail(null);
    },
    onError: (error) => {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add order",
        {
          variant: "error",
        }
      );
    },
  });

  const handleGroupSelect = (item) => {
    setSelectedGroupItem((ps) => ({
      ...ps,
      [item.categoryID]: item.productID,
    }));

    // Ensure qty is set (default 1)
    setOptionalQty((prevQty) => ({
      ...prevQty,
      [item.productID]: prevQty[item.productID] || item.qty || 1,
    }));
  };

  const handleChangeQty = (e, item) => {
    const { value } = e.target;
    const numValue = parseInt(value, 10) || 0;

    // Validate against min/max limits
    const minQty = item?.minQty || 1;
    const maxQty = item?.maxQty || item?.qty;

    if (value === "" || (numValue >= minQty && numValue <= maxQty)) {
      setOptionalQty((ps) => ({ ...ps, [item.productID]: value }));
    }
  };

  useEffect(() => {
    Object?.values(packageDetail || {})
      ?.flat(1)
      .forEach((el) => {
        setOptionalQty((ps) => ({
          ...ps,
          [el.productID]: ps[el.productID] || el.qty || 1, // Default qty to 1 if not set
        }));
      });
  }, [packageDetail]);

  const onSubmit = (data) => {
    const optionalProducts =
      selectedPackage?.map((el) => ({
        productID: el.productID,
        qty: +optionalQty[el.productID] || 1,
      })) || [];

    if (isAllOptional && (!optionalProducts || optionalProducts.length === 0)) {
      enqueueSnackbar("Select a Product", { variant: "error" });
      return;
    }
    // Ensure at least one radio button selection is made
    if (
      groupProduct.length > 0 &&
      Object.keys(selectedGroupItem).length === 0
    ) {
      enqueueSnackbar(
        "Please select at least one product from the mandatory group options before proceeding.",
        {
          variant: "error",
        }
      );
      return;
    }
    const filterData = Object?.values(packageDetail || {})
      ?.flat(1)
      .filter((el) => el.optional === 2);

    const mandatoryProducts = filterData.map((el) => ({
      productID: el.productID,
      qty: +optionalQty[el.productID] || 1, // Default qty to 1 if not set
    }));
    const mandatoryGroupProducts = Object.entries(selectedGroupItem).map(
      ([categoryID, productID]) => {
        const product = groupProduct.find((el) => el.productID === productID);
        return {
          productID: productID,
          qty: +optionalQty[productID] || 1, // Default to 1 if missing
        };
      }
    );
    const req = {
      paymentMode: data.paymentMode,
      bankRefNo: data.bankRefNo,
      studentID: studentWatch?.studentID,
      packageID: packageWatch.packageID,
      optionalProducts,
      mandatoryProducts:
        groupProduct.length > 0
          ? [...mandatoryProducts, ...mandatoryGroupProducts]
          : mandatoryProducts,
    };
    mutation.mutate({ req });
  };

  const groupTotal = groupProduct
    .filter((el) => Object.values(selectedGroupItem).includes(el.productID))
    .reduce(
      (acc, crr) => acc + (optionalQty[crr.productID] || 1) * crr.price,
      0
    );
  const optionalTotal =
    selectedPackage.reduce(
      (acc, crr) => acc + optionalQty[crr.productID] * crr?.price,
      0
    ) + groupTotal;
  const mendatoryPrice = Object?.values(packageDetail || {})
    ?.flat(1)
    ?.filter((el) => el.optional === 1)
    ?.reduce((acc, crr) => acc + crr?.amount, 0);
  const mendatoryEditable = Object?.values(packageDetail || {})
    ?.flat(1)
    ?.filter((el) => el.optional === 2)
    .reduce((acc, crr) => acc + optionalQty[crr.productID] * crr?.price, 0);

  // Calculate if checkbox column should be shown
  const hasCheckboxColumn = !isMendatory || groupProduct.length > 0;

  return (
    <Page title="Master">
      <Container maxWidth={themeStretch ? false : "xl"}>
        <HeaderBreadcrumbs
          heading={`Create Order`}
          links={[
            { name: "Dashboard", href: PATH_DASHBOARD.root },
            { name: "Create Order" },
          ]}
        />
        <FormProvider onSubmit={handleSubmit(onSubmit)} methods={methods}>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <RHFAutocomplete
                name={"student"}
                size="small"
                label="Select Student"
                loading={isLoading}
                option={studentList?.data || []}
                getOptionLabel={(option) =>
                  `${option?.firstName} ${option?.lastName} (${option?.studentCode})`
                }
                onInputChange={(value) => {
                  setValue("package", "");
                  setPackageDetail(null);
                }}
                isOptionEqualToValue={(option, value) =>
                  option.studentID === value.studentID
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <RHFAutocomplete
                name={"package"}
                size="small"
                label="Select Package"
                option={packageList || []}
                loading={isLoadingPackage}
                getOptionLabel={(option) => option.packageName}
                onInputChange={(value) => {
                  fetchPackageDetails(value);
                }}
                isOptionEqualToValue={(option, value) =>
                  option.packageID === value.packageID
                }
              />
            </Grid>
          </Grid>
          <div style={{ marginTop: 16 }}>
            {packageDetail && (
              <>
                <TableContainer sx={{ mb: 2 }} component={Paper} elevation={2}>
                  <Table
                    size={"medium"}
                    stickyHeader
                    sx={{
                      "& .MuiTableCell-root": {
                        py: 1,
                        verticalAlign: "top",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {(!isMendatory || groupProduct.length > 0) && (
                          <TableCell sx={{ width: 60 }}>
                            <Checkbox
                              checked={
                                selectedPackage?.length ===
                                Object.values(packageDetail)
                                  ?.flat(1)
                                  ?.filter(
                                    (el) =>
                                      el?.optional === 3 || el?.optional === 4
                                  )?.length
                              }
                              onChange={handleChangeAll}
                              indeterminate={
                                selectedPackage > 0 &&
                                selectedPackage?.length <
                                  Object.values(packageDetail)?.flat(1)?.length
                              }
                              size="small"
                            />
                          </TableCell>
                        )}

                        <TableCell sx={{ width: 250 }}>PRODUCT</TableCell>
                        <TableCell sx={{ width: 180 }}>AUTHOR / PUB.</TableCell>
                        <TableCell sx={{ width: 150 }}>
                          Single Qty Price
                        </TableCell>
                        <TableCell sx={{ width: 150 }}>Qty</TableCell>
                        <TableCell sx={{ width: 140 }}>Total Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object?.keys(packageDetail)?.map((el, mainIndex) => (
                        <React.Fragment key={el}>
                          {/* Category header row */}
                          <TableRow
                            onClick={() => handleCategoryClick(el)}
                            sx={{
                              cursor: "pointer",
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark"
                                  ? theme.palette.grey[800]
                                  : theme.palette.grey[300],
                            }}
                          >
                            {(!isMendatory || groupProduct.length > 0) && (
                              <TableCell
                                sx={{
                                  width: 60,
                                  backgroundColor: "inherit",
                                }}
                              >
                                <IconButton size="small">
                                  {expandedCategory === el ? (
                                    <ExpandLess />
                                  ) : (
                                    <ExpandMore />
                                  )}
                                </IconButton>
                              </TableCell>
                            )}

                            <TableCell
                              colSpan={
                                !isMendatory || groupProduct.length > 0 ? 5 : 6
                              }
                              sx={{ backgroundColor: "inherit" }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                {!isMendatory ||
                                groupProduct.length > 0 ? null : (
                                  <IconButton size="small">
                                    {expandedCategory === "all" ||
                                    expandedCategory === el ? (
                                      <ExpandLess />
                                    ) : (
                                      <ExpandMore />
                                    )}
                                  </IconButton>
                                )}
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    color: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? theme.palette.common.white
                                        : theme.palette.common.black,
                                  }}
                                >
                                  {el}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Product rows */}
                          {(expandedCategory === "all" ||
                            expandedCategory === el) &&
                            packageDetail[el].map((item, index) => (
                              <TableRow
                                key={`${el}-${item.productID}-${index}`}
                              >
                                {(!isMendatory || groupProduct.length > 0) && (
                                  <TableCell sx={{ width: 60 }}>
                                    {(item.optional === 3 ||
                                      item.optional === 4) && (
                                      <Checkbox
                                        onChange={() =>
                                          handleChangeSingle(item)
                                        }
                                        checked={selectedPackage?.includes(
                                          item
                                        )}
                                        size="small"
                                      />
                                    )}

                                    {item.optional === 5 && (
                                      <Radio
                                        checked={
                                          selectedGroupItem[
                                            item?.categoryID
                                          ] === item.productID
                                        }
                                        onChange={() => handleGroupSelect(item)}
                                        value={
                                          selectedGroupItem[item.categoryID]
                                        }
                                        name={`radio-${item.categoryID}`}
                                        size="small"
                                      />
                                    )}
                                  </TableCell>
                                )}

                                <TableCell>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                  >
                                    {item.productName}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontStyle: "italic",
                                      color: "text.secondary",
                                      mt: 0.5,
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {item.description &&
                                    item.description.trim() !== "null" &&
                                    item.description.trim() !== "0"
                                      ? item.description
                                      : "No description available"}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Typography variant="body2">
                                    {item?.authorName &&
                                    item.authorName.trim() &&
                                    item.authorName !== "null" &&
                                    item.authorName !== "0"
                                      ? item.authorName
                                      : "-"}
                                    /
                                    {item?.publicationName &&
                                    item.publicationName.trim() &&
                                    item.publicationName !== "null" &&
                                    item.publicationName !== "0"
                                      ? item.publicationName
                                      : "-"}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <div className="style-4">
                                    {item.discountPercent > 0 &&
                                      item.discountPercent < 100 && (
                                        <Box
                                          component="span"
                                          sx={{ display: "block" }}
                                        >
                                          <Box
                                            component="del"
                                            sx={{
                                              color: "text.secondary",
                                              fontSize: "0.8rem",
                                            }}
                                          >
                                            ₹
                                            {(
                                              item.price /
                                              (1 - item.discountPercent / 100)
                                            ).toFixed(0)}
                                          </Box>
                                        </Box>
                                      )}

                                    {item.discountPercent > 0 && (
                                      <Typography
                                        variant="caption"
                                        color="success.main"
                                      >
                                        {item.discountPercent}% Discount
                                      </Typography>
                                    )}

                                    <Box
                                      component="ins"
                                      sx={{ textDecoration: "none" }}
                                    >
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                      >
                                        ₹{fString(item.price)}
                                      </Typography>
                                    </Box>
                                  </div>
                                </TableCell>

                                <TableCell>
                                  {item.optional === 2 ||
                                  item.optional === 4 ? (
                                    <TextField
                                      type="number"
                                      size="small"
                                      label="Qty"
                                      sx={{ width: "100%", maxWidth: 120 }}
                                      disabled={
                                        selectedPackage.indexOf(item) === -1 &&
                                        item.optional === 4
                                      }
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{
                                        min: item?.minQty || 1,
                                        max: item?.maxQty || item?.qty,
                                        maxLength: 3,
                                      }}
                                      value={optionalQty[item.productID]}
                                      InputProps={{
                                        endAdornment: item.maxQty && (
                                          <InputAdornment position="end">
                                            <Typography variant="caption">
                                              /{item.maxQty}
                                            </Typography>
                                          </InputAdornment>
                                        ),
                                      }}
                                      onChange={(e) => handleChangeQty(e, item)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }
                                      }}
                                      helperText={`Min: ${
                                        item?.minQty || 1
                                      }, Max: ${item?.maxQty || item?.qty}`}
                                      FormHelperTextProps={{
                                        sx: { fontSize: "0.65rem", mt: 0 },
                                      }}
                                    />
                                  ) : (
                                    <Typography>{item.qty}</Typography>
                                  )}
                                </TableCell>

                                <TableCell>
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                  >
                                    ₹
                                    {fString(
                                      optionalQty[item.productID] * item.price
                                    )}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        {(!isMendatory || groupProduct.length > 0) && (
                          <TableCell sx={{ width: 60 }} />
                        )}
                        <TableCell colSpan={3} />
                        <TableCell colSpan={2}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center", // <-- vertically center
                              width: "100%",
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={600}>
                              Optional Items:{" "}
                              {selectedPackage?.length +
                                Object.keys(selectedGroupItem).length || 0}
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700}>
                              Total: ₹{" "}
                              {fString(
                                optionalTotal +
                                  mendatoryPrice +
                                  mendatoryEditable || 0
                              )}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>

                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={3}>
                    <RHFSelect
                      name={"paymentMode"}
                      native={false}
                      size="small"
                      label="Payment Mode"
                      fullWidth
                    >
                      <MenuItem value="">Select Payment</MenuItem>
                      <MenuItem value={"Credit Card"}>Credit Card</MenuItem>
                      <MenuItem value={"Debit Card"}>Debit Card</MenuItem>
                      <MenuItem value={"Net Banking"}>Net Banking</MenuItem>
                      <MenuItem value={"UPI"}>UPI</MenuItem>
                      <MenuItem value={"cash"}>Cash</MenuItem>
                    </RHFSelect>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <RHFTextField
                      label="Transaction ID"
                      name={"bankRefNo"}
                      placeholder="Bank Ref No/Transaction ID"
                      disabled={paymentModeWatch === "cash"}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      loading={mutation.isLoading}
                      type="submit"
                      sx={{ minWidth: 140 }}
                    >
                      Create Order
                    </LoadingButton>
                  </Grid>
                </Grid>
              </>
            )}
            {loading && <LinearProgress />}
          </div>
        </FormProvider>
      </Container>
    </Page>
  );
}
